import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Users, Plus, Loader2, Download, Sparkles, Heart, Target, Building2,
  Wallet, AlertTriangle, MessageSquareText, Lightbulb, GraduationCap, Compass,
  ShieldCheck, Smartphone, CheckCircle2,
} from "lucide-react";

function MobileVerifyGate({ onVerified }) {
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (mobile.replace(/\D/g, "").length < 10) { toast.info("Enter a valid 10-digit mobile"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/otp/send", { mobile });
      setDemoOtp(data.demo_otp || "");
      setStep(2);
      toast.success("OTP sent to your mobile");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send OTP"); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    if (otp.length < 6) { toast.info("Enter the 6-digit OTP"); return; }
    setLoading(true);
    try {
      await api.post("/otp/verify", { mobile, otp });
      toast.success("Mobile verified!");
      await onVerified();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="parent-mobile-verify" className="mt-6 rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-[#0F2A33] to-[#131C2E] p-6">
      <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-cyan-400" />
        <h3 className="font-display text-lg font-semibold">Verify your mobile number</h3></div>
      <p className="mt-2 text-sm text-slate-400 max-w-lg">
        For your child's safety, we verify every parent's mobile before granting access to their career profile.
      </p>
      {step === 1 ? (
        <div className="mt-4 flex flex-wrap gap-3">
          <div className="flex-1 min-w-[220px] flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3">
            <Smartphone className="h-4 w-4 text-slate-500" />
            <input data-testid="parent-mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, "").slice(0, 13))}
              placeholder="10-digit mobile" className="flex-1 bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-slate-500" />
          </div>
          <button data-testid="parent-send-otp-btn" onClick={send} disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-bold text-[#041016] disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Send OTP</>}
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-3 max-w-sm">
          <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3 text-xs text-cyan-300">
            We sent a 6-digit OTP to {mobile}. {demoOtp && <span className="font-bold">Demo OTP: {demoOtp}</span>}
          </div>
          <input data-testid="parent-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="••••••" maxLength={6}
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-center font-mono-data text-2xl tracking-[0.5em] text-white outline-none focus:border-cyan-400/50" />
          <button data-testid="parent-verify-otp-btn" onClick={verify} disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-400 px-6 py-3 text-sm font-bold text-[#041016] disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" /> Verify &amp; Continue</>}
          </button>
          <button onClick={() => setStep(1)} className="w-full text-xs text-slate-400 hover:text-white">← Change number</button>
        </div>
      )}
    </div>
  );
}

export default function ParentDashboard() {
  const { user, refresh } = useAuth();
  const [children, setChildren] = useState([]);
  const [active, setActive] = useState(null);
  const [email, setEmail] = useState("");
  const [linking, setLinking] = useState(false);

  const load = () => api.get("/parent/children").then((r) => { setChildren(r.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const link = async () => {
    if (!email) { toast.info("Enter your child's registered email"); return; }
    setLinking(true);
    try {
      await api.post("/parent/link", { student_email: email });
      toast.success("Request sent! Ask your child to approve it.");
      setEmail(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send request"); }
    finally { setLinking(false); }
  };

  const approved = children.filter((c) => c.status === "approved");
  const verified = !!user?.mobile_verified;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">Parent Dashboard</p>
      <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight">My Child's Career</h1>

      {!verified ? (
        <MobileVerifyGate onVerified={refresh} />
      ) : (
      <>
      {/* Link child */}
      <div className="mt-6 rounded-2xl border border-white/8 bg-[#131C2E] p-5">
        <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Plus className="h-4 w-4 text-cyan-400" /> Link a student</p>
        <div className="flex flex-wrap gap-3">
          <input data-testid="link-child-email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Child's registered email" type="email"
            className="flex-1 min-w-[220px] rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
          <button data-testid="link-child-btn" onClick={link} disabled={linking}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-bold text-[#041016] disabled:opacity-50">
            {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Send request</>}
          </button>
        </div>
        {children.filter((c) => c.status !== "approved").length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {children.filter((c) => c.status !== "approved").map((c) => (
              <span key={c.link_id} className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">
                {c.student_name}: <span className="capitalize text-amber-300">{c.status}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {approved.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-white/8 bg-[#131C2E] p-8 text-center">
          <Users className="mx-auto mb-3 h-8 w-8 text-cyan-400" />
          <p className="text-white font-semibold">No linked children yet</p>
          <p className="text-sm text-slate-400 mt-1">Send a request above — your child approves it and controls what you can see.</p>
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap gap-2">
            {approved.map((c) => (
              <button key={c.student_id} data-testid="child-tab" onClick={() => setActive(c.student_id)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                  (active || approved[0].student_id) === c.student_id ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-300"
                }`}>{c.student_name}</button>
            ))}
          </div>
          <ChildView sid={active || approved[0].student_id} />
        </>
      )}
      </>
      )}
    </div>
  );
}

function ChildView({ sid }) {
  const [profile, setProfile] = useState(null);
  const [insights, setInsights] = useState(null);
  const [discussion, setDiscussion] = useState(null);
  const [tab, setTab] = useState("profile");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setProfile(null); setInsights(null); setDiscussion(null); setTab("profile");
    api.get(`/parent/child/${sid}/profile`).then((r) => setProfile(r.data)).catch((e) => setProfile({ error: e.response?.data?.detail || "No access" }));
    api.get(`/parent/child/${sid}/insights`).then((r) => setInsights(r.data)).catch(() => {});
    api.get(`/parent/child/${sid}/discussion`).then((r) => setDiscussion(r.data)).catch(() => {});
  }, [sid]);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("pf_token");
      const res = await fetch(`${API}/parent/child/${sid}/report`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error("Report unavailable"); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "My_Childs_Career_Report.pdf";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  if (!profile) return <div className="mt-6 text-slate-400">Loading...</div>;
  if (profile.error) return <div className="mt-6 rounded-2xl border border-rose-400/20 bg-rose-400/5 p-6 text-rose-300">{profile.error}</div>;
  if (!profile.assessment_completed) return <div className="mt-6 rounded-2xl border border-amber-400/20 bg-amber-400/5 p-6 text-amber-300">Your child hasn't completed the AI assessment yet.</div>;

  return (
    <div className="mt-6" data-testid="child-view">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex gap-2">
          {[["profile", "Profile"], ["insights", "AI Insights"], ["discussion", "Career Discussion"]].map(([k, l]) => (
            <button key={k} data-testid={`parent-tab-${k}`} onClick={() => setTab(k)}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="download-child-report-btn" onClick={downloadReport} disabled={downloading}
          className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-300 disabled:opacity-60">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Career Report PDF
        </button>
      </div>

      {tab === "profile" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card title="Career Clarity" icon={Compass}>
            <div className="flex items-center gap-3">
              <span className="font-mono-data text-3xl font-extrabold text-cyan-300">{profile.clarity_score}%</span>
              <div className="h-2 flex-1 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400" style={{ width: `${profile.clarity_score}%` }} />
              </div>
            </div>
          </Card>
          <Card title="Strengths" icon={Sparkles}>
            <Tags items={profile.strengths} color="emerald" />
          </Card>
          <Card title="Potential Careers" icon={Target}>
            {profile.recommended_careers.map((c) => (
              <div key={c.title} className="flex justify-between text-sm py-0.5">
                <span className="text-white">{c.title}</span><span className="font-mono-data text-cyan-300">{c.match}%</span>
              </div>
            ))}
          </Card>
          <Card title="Recommended Subjects & Exams" icon={GraduationCap}>
            <Tags items={[...profile.recommended_streams, ...profile.recommended_exams]} color="indigo" />
          </Card>
          {profile.colleges?.length > 0 && (
            <Card title="Recommended Colleges" icon={Building2}>
              {profile.colleges.slice(0, 5).map((c) => (
                <div key={c.name} className="flex justify-between text-sm py-0.5">
                  <span className="text-white truncate">{c.name}</span><span className="font-mono-data text-emerald-300">{c.probability}%</span>
                </div>
              ))}
            </Card>
          )}
          {profile.estimated_cost && (
            <Card title="Estimated Education Cost" icon={Wallet}>
              <p className="font-display text-xl font-bold text-white">{profile.estimated_cost}</p>
            </Card>
          )}
        </div>
      )}

      {tab === "insights" && (
        insights ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-cyan-400/20 bg-gradient-to-r from-[#0F2A33] to-[#131C2E] p-5">
              <p className="text-sm text-slate-200 leading-relaxed">{insights.summary}</p>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <Card title="Natural Interests" icon={Heart}><Tags items={insights.natural_interests} color="rose" /></Card>
              <Card title="Where Strengths Fit" icon={Sparkles}><Tags items={insights.strength_fit} color="emerald" /></Card>
              <Card title="Paths to Explore" icon={Target}><Bullets items={insights.paths_to_explore} /></Card>
              <Card title="Skills to Develop" icon={Lightbulb}><Bullets items={insights.skills_to_develop} /></Card>
              <Card title="Financial Considerations" icon={Wallet}><p className="text-sm text-slate-300">{insights.financial_considerations}</p></Card>
              <Card title="Questions to Discuss" icon={MessageSquareText}><Bullets items={insights.questions_to_discuss} /></Card>
            </div>
            <div className="rounded-2xl border border-amber-400/25 bg-amber-400/5 p-5">
              <p className="text-sm font-semibold text-amber-300 flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4" /> What to avoid</p>
              <Bullets items={insights.what_to_avoid} amber />
            </div>
            <p className="text-[11px] text-slate-500 italic">AI guidance for discussion only — not a guaranteed outcome or psychological assessment.</p>
          </div>
        ) : <Loading />
      )}

      {tab === "discussion" && (
        discussion ? (
          <div className="grid lg:grid-cols-2 gap-4">
            <Card title="Where You Likely Agree" icon={Heart}><Bullets items={discussion.alignment_points} /></Card>
            <Card title="Possible Disagreements" icon={AlertTriangle}><Bullets items={discussion.possible_disagreements} /></Card>
            <Card title="Options to Explore Together" icon={Target}><Bullets items={discussion.options_to_understand} /></Card>
            <Card title="Questions for a Counsellor" icon={MessageSquareText}><Bullets items={discussion.questions_for_counsellor} /></Card>
            <div className="lg:col-span-2">
              <Card title="Steps to a Mutual Roadmap" icon={Compass}><Bullets items={discussion.agreed_roadmap_steps} /></Card>
            </div>
          </div>
        ) : <Loading />
      )}
    </div>
  );
}

const Loading = () => <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /><p className="mt-2 text-xs text-slate-500">AI is preparing insights...</p></div>;
const Card = ({ title, icon: Icon, children }) => (
  <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-5">
    <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-white"><Icon className="h-4 w-4 text-cyan-400" /> {title}</p>
    {children}
  </div>
);
const Tags = ({ items, color }) => (
  <div className="flex flex-wrap gap-2">
    {(items || []).map((t, i) => <span key={i} className={`rounded-full px-2.5 py-1 text-xs border ${color === "emerald" ? "border-emerald-400/20 bg-emerald-400/5 text-emerald-300" : color === "rose" ? "border-rose-400/20 bg-rose-400/5 text-rose-300" : "border-indigo-400/20 bg-indigo-400/5 text-indigo-300"}`}>{t}</span>)}
  </div>
);
const Bullets = ({ items, amber }) => (
  <ul className="space-y-1.5">{(items || []).map((t, i) => <li key={i} className={`text-sm ${amber ? "text-amber-200" : "text-slate-300"} flex gap-2`}><span className="text-cyan-400">•</span> {t}</li>)}</ul>
);
